import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtDate, fmtDateTime, fmtINR } from "@/lib/api";
import { PageHeader, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import DocumentTrail from "@/components/DocumentTrail";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Send, X, ShoppingCart, Lock } from "lucide-react";
import { AdminVoidRestore, AdminEditBanner } from "@/components/AdminActions";

export default function IndentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasRole, fieldHidden, user } = useAuth();
  const [ind, setInd] = useState(null);
  const [audit, setAudit] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [reason, setReason] = useState("");
  const [poForm, setPoForm] = useState({
    vendor_id: "", vendor_name: "", vendor_address: "", vendor_gstin: "",
    delivery_date: "", delivery_address: "", payment_terms: "30 days credit",
    freight: 0, other_charges: 0, terms_conditions: "", remarks: "",
    project_name: "", site_name: "",
    // Advance
    advance_type: "none", advance_percent: 0, advance_amount: 0,
    advance_payment_mode: "NEFT", advance_utr: "", advance_bank: "", advance_date: "",
    items: [],
  });

  const load = async () => {
    const r = await api.get(`/indents/${id}`);
    setInd(r.data);
    setPoForm((f) => ({
      ...f,
      project_name: r.data.project_name || "",
      site_name: r.data.site_name || "",
      items: (r.data.items || []).map((it) => ({
        material_name: it.material_name,
        description: it.description || "",
        unit: it.unit,
        qty: it.required_qty,
        rate: 0,
        gst_pct: 18,
        amount: 0, gst_amount: 0, total: 0,
      })),
    }));
    try { const a = await api.get(`/audit-logs?entity_id=${id}`); setAudit(a.data); }
    catch (e) { console.warn("Failed to load audit trail:", e); }
  };
  useEffect(() => { load(); api.get("/masters/vendors").then((v) => setVendors(v.data)); }, [id]);

  if (!ind) return <div>Loading…</div>;

  const canFreeze = ind.status === "Draft" && (
    hasRole("Administrator", "Indent Creator", "Site Incharge", "Project Manager") ||
    ind.created_by === user?.id
  );
  const canCreatePO = ind.status === "Frozen" && hasRole("Administrator", "Purchase Executive", "Purchase Manager");
  const canReject = ind.status === "Frozen" && hasRole("Administrator", "QS Engineer", "Purchase Manager");

  const freeze = async () => {
    try {
      await api.post(`/indents/${id}/freeze`);
      toast.success("Requirement frozen. Ready for PO.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    try {
      await api.post(`/indents/${id}/reject`, { reason });
      toast.success("Rejected");
      setRejectOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createPO = async () => {
    if (!poForm.vendor_id) { toast.error("Select vendor"); return; }
    try {
      const { data } = await api.post("/pos", { indent_id: id, ...poForm });
      toast.success(`PO ${data.po_number} created`);
      setPoOpen(false);
      nav(`/pos/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="indent-detail">
      <PageHeader
        title={<span className="font-mono">{ind.indent_no}</span>}
        subtitle={`${ind.project_name} • ${ind.site_name}`}
        actions={
          <>
            {canFreeze && <PrimaryButton onClick={freeze} data-testid="freeze-btn"><Lock size={14} className="mr-2" />Freeze Requirement</PrimaryButton>}
            {canReject && <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="reject-btn"><X size={14} className="mr-2" />Reject</Button>}
            {canCreatePO && <PrimaryButton onClick={() => setPoOpen(true)} data-testid="create-po-btn"><ShoppingCart size={14} className="mr-2" />Create Purchase Order</PrimaryButton>}
            <AdminVoidRestore entity="indent" id={id} isVoided={ind.is_voided} onDone={load} />
          </>
        }
      />
      <AdminEditBanner doc={ind} />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Details</div>
              <div className="font-display text-xl font-semibold">{ind.purpose || "Material Requirement"}</div>
            </div>
            <StatusBadge status={ind.status} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] uppercase text-muted-foreground">Date</div>{fmtDate(ind.date)}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Required By</div>{fmtDate(ind.required_by_date)}</div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Priority</div><span className={ind.priority === "Emergency" ? "text-red-600 font-semibold" : ind.priority === "Urgent" ? "text-amber-600 font-semibold" : ""}>{ind.priority}</span></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Created By</div>{ind.created_by_name}</div>
          </div>
          {ind.rejection_reason && (
            <div className="mt-4 p-3 rounded-md bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 text-sm border border-red-200 dark:border-red-900">
              <strong>Rejection Reason:</strong> {ind.rejection_reason}
            </div>
          )}
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Line Items</div>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Material</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-right px-3 py-2">Stock</th>
                    {!fieldHidden("indent", "items.estimated_cost") && <th className="text-right px-3 py-2">Est. Cost</th>}
                  </tr>
                </thead>
                <tbody>
                  {ind.items.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{it.material_name}</td>
                      <td className="px-3 py-2">{it.unit}</td>
                      <td className="px-3 py-2 text-right">{it.required_qty}</td>
                      <td className="px-3 py-2 text-right">{it.current_stock}</td>
                      {!fieldHidden("indent", "items.estimated_cost") && <td className="px-3 py-2 text-right font-mono">{fmtINR(it.estimated_cost)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {ind.remarks && <div className="mt-4 text-sm"><span className="text-muted-foreground">Remarks: </span>{ind.remarks}</div>}
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">Activity</div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {audit.length === 0 && <div className="text-xs text-muted-foreground">No activity yet.</div>}
            {audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-kabir pl-3">
                <div className="font-semibold capitalize">{a.action}</div>
                <div className="text-muted-foreground">by {a.user_name} • {fmtDateTime(a.created_at)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <DocumentTrail trail={ind.trail_ref} />

      {/* Reject */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Requirement</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} data-testid="confirm-reject">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create PO */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Purchase Order from Requirement {ind.indent_no}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold">Vendor *</Label>
              <Select value={poForm.vendor_id} onValueChange={(v) => {
                const vv = vendors.find((x) => x.id === v);
                setPoForm({ ...poForm, vendor_id: v, vendor_name: vv?.name || "", vendor_address: vv?.extra?.address || "", vendor_gstin: vv?.extra?.gstin || "" });
              }}>
                <SelectTrigger data-testid="po-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase font-semibold">Delivery Date</Label><Input type="date" value={poForm.delivery_date} onChange={(e) => setPoForm({ ...poForm, delivery_date: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Payment Terms</Label><Input value={poForm.payment_terms} onChange={(e) => setPoForm({ ...poForm, payment_terms: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Delivery Address</Label><Input value={poForm.delivery_address} onChange={(e) => setPoForm({ ...poForm, delivery_address: e.target.value })} /></div>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-right px-2 py-2 w-20">Qty</th>
                  <th className="text-right px-2 py-2 w-28">Rate</th>
                  <th className="text-right px-2 py-2 w-16">GST%</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {poForm.items.map((it, i) => {
                  const total = (parseFloat(it.qty) || 0) * (parseFloat(it.rate) || 0) * (1 + (parseFloat(it.gst_pct) || 0) / 100);
                  return (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{it.material_name}</td>
                      <td className="px-1 py-1"><Input type="number" className="h-8 text-right" value={it.qty} onChange={(e) => setPoForm({ ...poForm, items: poForm.items.map((x, idx) => idx === i ? { ...x, qty: parseFloat(e.target.value) || 0 } : x) })} /></td>
                      <td className="px-1 py-1"><Input type="number" className="h-8 text-right" value={it.rate} onChange={(e) => setPoForm({ ...poForm, items: poForm.items.map((x, idx) => idx === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x) })} data-testid={`po-item-rate-${i}`} /></td>
                      <td className="px-1 py-1"><Input type="number" className="h-8 text-right" value={it.gst_pct} onChange={(e) => setPoForm({ ...poForm, items: poForm.items.map((x, idx) => idx === i ? { ...x, gst_pct: parseFloat(e.target.value) || 0 } : x) })} /></td>
                      <td className="px-3 py-2 text-right font-mono">{fmtINR(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Textarea placeholder="Terms & conditions…" value={poForm.terms_conditions} onChange={(e) => setPoForm({ ...poForm, terms_conditions: e.target.value })} rows={2} />

          {/* ---- Advance section ---- */}
          <div className="rounded-md border border-kabir/40 bg-kabir/5 p-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-kabir-dark mb-2">Vendor Advance (optional)</div>
            <div className="grid md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase font-semibold">Type</Label>
                <Select value={poForm.advance_type} onValueChange={(v) => setPoForm({ ...poForm, advance_type: v })}>
                  <SelectTrigger data-testid="po-advance-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No advance</SelectItem>
                    <SelectItem value="percent">% of PO total</SelectItem>
                    <SelectItem value="amount">Fixed amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {poForm.advance_type === "percent" && (
                <div>
                  <Label className="text-xs uppercase font-semibold">Advance %</Label>
                  <Input type="number" value={poForm.advance_percent} onChange={(e) => setPoForm({ ...poForm, advance_percent: parseFloat(e.target.value) || 0 })} data-testid="po-advance-percent" />
                </div>
              )}
              {poForm.advance_type === "amount" && (
                <div>
                  <Label className="text-xs uppercase font-semibold">Amount (₹)</Label>
                  <Input type="number" value={poForm.advance_amount} onChange={(e) => setPoForm({ ...poForm, advance_amount: parseFloat(e.target.value) || 0 })} data-testid="po-advance-amount" />
                </div>
              )}
              {poForm.advance_type !== "none" && (
                <>
                  <div>
                    <Label className="text-xs uppercase font-semibold">Mode</Label>
                    <Select value={poForm.advance_payment_mode} onValueChange={(v) => setPoForm({ ...poForm, advance_payment_mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NEFT">NEFT</SelectItem>
                        <SelectItem value="RTGS">RTGS</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase font-semibold">UTR / Chq (if pre-paid)</Label>
                    <Input value={poForm.advance_utr} onChange={(e) => setPoForm({ ...poForm, advance_utr: e.target.value })} placeholder="Leave blank to route through Accounts" />
                  </div>
                </>
              )}
            </div>
            {poForm.advance_type !== "none" && !poForm.advance_utr && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                Leave UTR blank if Accounts will disburse this advance. It will appear as a pending &quot;Advance PV&quot; on the PO detail page and reconcile automatically with Tally on payment.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={createPO} data-testid="confirm-create-po">Issue Purchase Order</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
